export const START_IMAGE_HISTORY_LIMIT = 99

export interface StartImageHistoryEntry {
  id: string
  name: string
  type: string
  size: number
  lastModified: number
  createdAt: number
}

interface StoredStartImageMeta extends StartImageHistoryEntry {
  key: string
  identity: string
}

interface StoredDraftEndImage {
  key: string
  identity: string
  segmentId: string
  name: string
  type: string
  lastModified: number
  bytes: ArrayBuffer
}

const DATABASE_NAME = 'mugen2-image-assets'
const META_STORE = 'start-history-meta'
const BLOB_STORE = 'start-history-blobs'
const END_IMAGE_STORE = 'draft-end-images'

const normalizedIdentity = (identity: string) => identity || 'local'
const recordKey = (identity: string, id: string) => `${normalizedIdentity(identity)}\u0000${id}`

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, 1)
  request.addEventListener('upgradeneeded', () => {
    if (!request.result.objectStoreNames.contains(META_STORE)) request.result.createObjectStore(META_STORE, { keyPath: 'key' })
    if (!request.result.objectStoreNames.contains(BLOB_STORE)) request.result.createObjectStore(BLOB_STORE)
    if (!request.result.objectStoreNames.contains(END_IMAGE_STORE)) request.result.createObjectStore(END_IMAGE_STORE, { keyPath: 'key' })
  })
  request.addEventListener('success', () => resolve(request.result))
  request.addEventListener('error', () => reject(request.error ?? new Error('Image database open failed')))
})

const requestResult = <T,>(request: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  request.addEventListener('success', () => resolve(request.result))
  request.addEventListener('error', () => reject(request.error ?? new Error('IndexedDB request failed')))
})

const transactionDone = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.addEventListener('complete', () => resolve())
  transaction.addEventListener('abort', () => reject(transaction.error ?? new Error('IndexedDB transaction aborted')))
  transaction.addEventListener('error', () => reject(transaction.error ?? new Error('IndexedDB transaction failed')))
})

const asArrayBuffer = (value: unknown): ArrayBuffer | null => {
  if (value instanceof ArrayBuffer || Object.prototype.toString.call(value) === '[object ArrayBuffer]') return value as ArrayBuffer
  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer
  }
  return null
}

const readBlob = (blob: Blob): Promise<ArrayBuffer> => {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result as ArrayBuffer))
    reader.addEventListener('error', () => reject(reader.error ?? new Error('Image read failed')))
    reader.readAsArrayBuffer(blob)
  })
}

async function imageContentId(file: File, bytes: ArrayBuffer): Promise<string> {
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return `${file.name}\u0000${file.size}\u0000${file.lastModified}`
}

const sortEntries = (entries: StartImageHistoryEntry[]): StartImageHistoryEntry[] =>
  [...entries].sort((left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id))

export async function listStartImages(identity: string): Promise<StartImageHistoryEntry[]> {
  const database = await openDatabase()
  try {
    const transaction = database.transaction(META_STORE, 'readonly')
    const records = await requestResult(transaction.objectStore(META_STORE).getAll()) as StoredStartImageMeta[]
    await transactionDone(transaction)
    return sortEntries(records
      .filter((record) => record.identity === normalizedIdentity(identity))
      .map(({ key: _key, identity: _identity, ...entry }) => entry))
  } finally {
    database.close()
  }
}

export async function addStartImage(identity: string, file: File): Promise<{
  entries: StartImageHistoryEntry[]
  selectedId: string
}> {
  const owner = normalizedIdentity(identity)
  const bytes = await readBlob(file)
  const id = await imageContentId(file, bytes)
  const current = await listStartImages(owner)
  const createdAt = Math.max(Date.now(), (current.at(-1)?.createdAt ?? 0) + 1)
  const entry: StartImageHistoryEntry = {
    id,
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    lastModified: file.lastModified,
    createdAt,
  }
  const next = sortEntries([...current.filter((item) => item.id !== id), entry])
  const evicted = next.slice(0, Math.max(0, next.length - START_IMAGE_HISTORY_LIMIT))
  const kept = next.slice(-START_IMAGE_HISTORY_LIMIT)
  const isNew = !current.some((item) => item.id === id)

  const database = await openDatabase()
  try {
    const transaction = database.transaction([META_STORE, BLOB_STORE], 'readwrite')
    transaction.objectStore(META_STORE).put({ ...entry, key: recordKey(owner, id), identity: owner } satisfies StoredStartImageMeta)
    if (isNew) transaction.objectStore(BLOB_STORE).put(bytes, recordKey(owner, id))
    evicted.forEach((item) => {
      const key = recordKey(owner, item.id)
      transaction.objectStore(META_STORE).delete(key)
      transaction.objectStore(BLOB_STORE).delete(key)
    })
    await transactionDone(transaction)
    return { entries: kept, selectedId: id }
  } finally {
    database.close()
  }
}

export async function loadStartImage(identity: string, entry: StartImageHistoryEntry): Promise<File | null> {
  const database = await openDatabase()
  try {
    const transaction = database.transaction(BLOB_STORE, 'readonly')
    const result = await requestResult(transaction.objectStore(BLOB_STORE).get(recordKey(identity, entry.id)))
    await transactionDone(transaction)
    const bytes = asArrayBuffer(result)
    if (!bytes) return null
    return new File([bytes], entry.name, { type: entry.type, lastModified: entry.lastModified })
  } finally {
    database.close()
  }
}

export async function clearStartImageHistory(identity: string): Promise<void> {
  const owner = normalizedIdentity(identity)
  const entries = await listStartImages(owner)
  const database = await openDatabase()
  try {
    const transaction = database.transaction([META_STORE, BLOB_STORE], 'readwrite')
    entries.forEach((entry) => {
      const key = recordKey(owner, entry.id)
      transaction.objectStore(META_STORE).delete(key)
      transaction.objectStore(BLOB_STORE).delete(key)
    })
    await transactionDone(transaction)
  } finally {
    database.close()
  }
}

export async function saveDraftEndImage(identity: string, segmentId: string, file?: File): Promise<void> {
  const owner = normalizedIdentity(identity)
  const key = recordKey(owner, segmentId)
  const bytes = file ? await readBlob(file) : null
  const database = await openDatabase()
  try {
    const transaction = database.transaction(END_IMAGE_STORE, 'readwrite')
    const store = transaction.objectStore(END_IMAGE_STORE)
    if (file) {
      store.put({
        key,
        identity: owner,
        segmentId,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        bytes: bytes!,
      } satisfies StoredDraftEndImage)
    } else {
      store.delete(key)
    }
    await transactionDone(transaction)
  } finally {
    database.close()
  }
}

export async function loadDraftEndImages(identity: string, segmentIds: string[]): Promise<Record<string, File>> {
  const owner = normalizedIdentity(identity)
  const database = await openDatabase()
  try {
    const transaction = database.transaction(END_IMAGE_STORE, 'readonly')
    const store = transaction.objectStore(END_IMAGE_STORE)
    const records = await Promise.all(segmentIds.map((segmentId) =>
      requestResult(store.get(recordKey(owner, segmentId))) as Promise<StoredDraftEndImage | undefined>,
    ))
    await transactionDone(transaction)
    return Object.fromEntries(records.flatMap((record) => {
      const bytes = asArrayBuffer(record?.bytes)
      return record && bytes
        ? [[record.segmentId, new File([bytes], record.name, { type: record.type, lastModified: record.lastModified })] as const]
        : []
    }))
  } finally {
    database.close()
  }
}

export async function pruneDraftEndImages(identity: string, segmentIds: string[]): Promise<void> {
  const owner = normalizedIdentity(identity)
  const valid = new Set(segmentIds.map((segmentId) => recordKey(owner, segmentId)))
  const database = await openDatabase()
  try {
    const readTransaction = database.transaction(END_IMAGE_STORE, 'readonly')
    const keys = await requestResult(readTransaction.objectStore(END_IMAGE_STORE).getAllKeys())
    await transactionDone(readTransaction)
    const stale = keys.filter((key) => typeof key === 'string' && key.startsWith(`${owner}\u0000`) && !valid.has(key))
    if (!stale.length) return
    const writeTransaction = database.transaction(END_IMAGE_STORE, 'readwrite')
    stale.forEach((key) => writeTransaction.objectStore(END_IMAGE_STORE).delete(key))
    await transactionDone(writeTransaction)
  } finally {
    database.close()
  }
}

export async function clearDraftEndImages(identity: string): Promise<void> {
  await pruneDraftEndImages(identity, [])
}
